import CustomToast from "components/atoms/CustomToast";
import { useDebateContext } from "contexts/DebateContext";
import { useDebateDetailContext } from "contexts/DebateDetailContext";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { CommentItem } from "pages/api/discuss/debate/[discussId]";
import { useEffect, useReducer, useState } from "react";
import { TYPO } from "styles/Typo";
import { likeComment } from "utils/clientAPIs/debate";
import { useReportCommentLike } from "lib/analytics/react/hooks/intent/report/useReportCommentLike";
import { useDiscussIdContext } from "contexts/DiscussIdContext";
import { toLoginPage } from "utils/routing";

interface State {
  clickCount: number;
  totalClicks: number;
  isBlocked: boolean;
}

type Action =
  | { type: "click" }
  | { type: "reset" }
  | { type: "block" }
  | { type: "unblock" };

const initialState: State = {
  clickCount: 0,
  totalClicks: 0,
  isBlocked: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "click":
      return {
        ...state,
        clickCount: state.clickCount + 1,
        totalClicks: state.totalClicks + 1,
      };
    case "reset":
      return {
        ...state,
        clickCount: 0,
      };
    case "block":
      return {
        ...state,
        isBlocked: true,
      };
    case "unblock":
      return {
        clickCount: 0,
        totalClicks: 0,
        isBlocked: false,
      };
    default:
      return state;
  }
}

function LikeButton({ data, type }: { data: CommentItem; type: string }) {
  const { mutate, triggerMutate } = useDebateContext();
  const { discussId } = useDiscussIdContext();
  const debateDetailContext = useDebateDetailContext();
  const likeCount = data?.like_count;
  const isLiked = data?.is_like; // 추후 옵티미스틱 적용
  const { data: session } = useSession();
  const router = useRouter();

  const reportCommentLike = useReportCommentLike();

  //클릭 횟수 제한
  const [clickBlockToastVisible, setClickBlockToastVisible] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  // 3초 후 초기화
  useEffect(() => {
    const timeouts = setTimeout(() => {
      dispatch({ type: "reset" });
    }, 3000);

    return () => {
      clearTimeout(timeouts);
    };
  }, [state]);

  // 옵티미스틱 업데이트
  const updateDebateDetailContext = (isLiked: boolean) => {
    const newData = debateDetailContext?.opinionDetail?.map((item: any) => {
      if (item.comment_id === data?.comment_id) {
        return {
          ...item,
          is_like: !isLiked,
          like_count: isLiked ? likeCount - 1 : likeCount + 1,
        };
      }
      return item;
    });

    debateDetailContext?.detailMutate({ data: newData }, false);
  };

  const updateDebateContext = (isLiked: boolean) => {
    mutate((prevData: any) => {
      if (!prevData) return prevData;

      const updatedPages = prevData.map((page: any) => {
        const updatedItems = page.data.map((item: any) => {
          if (item.comment_id === data?.comment_id) {
            return {
              ...item,
              is_like: !isLiked,
              like_count: isLiked ? likeCount - 1 : likeCount + 1,
            };
          }
          return item;
        });

        return { ...page, data: updatedItems };
      });

      return updatedPages;
    }, false);
  };

  const updateDebateCommentListContext = (isLiked: boolean) => {
    if (data.parent_id) {
      triggerMutate(data.parent_id);
    }
  };

  // 좋아요 클릭
  const handleLike = async (email?: string | null) => {
    if (!email) {
      // router.push(`/login?from=${router.asPath}`);
      toLoginPage(router, "report_comment_like");
      return;
    }

    // 클릭 횟수 제한
    dispatch({ type: "click" });

    if (state.clickCount >= 5) {
      dispatch({ type: "block" });
      setTimeout(() => dispatch({ type: "unblock" }), 30000);
    }
    if (state.totalClicks >= 10) {
      dispatch({ type: "block" });
      setTimeout(() => dispatch({ type: "unblock" }), 30000);
    }

    if (state.isBlocked) {
      setClickBlockToastVisible(true);
      return;
    }

    reportCommentLike.start({
      is_like: !isLiked,
      report_id: Number(discussId),
      comment_id: data?.comment_id,
    });
    if (type === "optionDetail") {
      updateDebateDetailContext(isLiked);
    }

    if (type !== "optionDetail") {
      updateDebateContext(isLiked);
      // 의견 디테일 '댓글'
      if (debateDetailContext) {
        updateDebateDetailContext(isLiked);
      }
    }

    // 실제 API
    try {
      const result = await likeComment(email, data?.comment_id, isLiked);
      const sleep = new Promise((resolve) => setTimeout(resolve, 150));
      await sleep;
      if (
        type === "comment" &&
        !router.pathname.startsWith("/discuss/[slugId]/opinion")
      ) {
        updateDebateCommentListContext(isLiked);
      }
      reportCommentLike.success();
    } catch (e) {
      reportCommentLike.fail();
    }
  };

  return (
    <div
      className="flex gap-1 items-center cursor-pointer p-1 debate-heart-button"
      onClick={(e: any) => {
        e.stopPropagation();
        handleLike(session?.user?.email);
      }}
      data-gtm-id={`report-${type}-like-button`}
    >
      <HeartIcon isActive={isLiked} />
      <span className={`${TYPO.TYPO7_RG} text-gray30`}>{likeCount}</span>

      <CustomToast
        message={
          router.locale === "ko"
            ? `짧은 시간에 너무 많이 반복했어요\n잠시 후 다시 시도해주세요.`
            : `Too many tries in a short period.\n Please try again later.`
        }
        isVisible={clickBlockToastVisible}
        isSuccess={false}
        onClose={() => setClickBlockToastVisible(false)}
        width={router.locale === "ko" ? 275 : 300}
      />
    </div>
  );
}

const HeartIcon = ({ isActive }: { isActive: boolean }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12 21C11.7783 21 11.5527 20.9599 11.3231 20.8798C11.0935 20.7996 10.8917 20.6714 10.7175 20.4951L9.07875 18.9802C7.40042 17.4253 5.88438 15.8824 4.53063 14.3515C3.17687 12.8206 2.5 11.1334 2.5 9.28996C2.5 7.78312 2.99875 6.52475 3.99625 5.51485C4.99375 4.50495 6.23667 4 7.725 4C8.56417 4 9.35583 4.18034 10.1 4.54102C10.8442 4.9017 11.4775 5.39463 12 6.0198C12.5225 5.39463 13.1558 4.9017 13.9 4.54102C14.6442 4.18034 15.4358 4 16.275 4C17.7633 4 19.0063 4.50495 20.0038 5.51485C21.0013 6.52475 21.5 7.78312 21.5 9.28996C21.5 11.1334 20.8271 12.8246 19.4812 14.3635C18.1354 15.9024 16.6075 17.4493 14.8975 19.0042L13.2825 20.4951C13.1083 20.6714 12.9065 20.7996 12.6769 20.8798C12.4473 20.9599 12.2217 21 12 21Z"
        fill={isActive ? "var(--red40)" : "var(--gray30)"}
      />
    </svg>
  );
};

export default LikeButton;
